import { FormEvent, useMemo, useState } from 'react';
import { ArrowUpRight, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { logFirebaseEvent } from '@/lib/tracking';
import { submitMarketingLead } from '@/lib/leadCapture';

export default function LeadMagnetSection() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const contactHref = useMemo(() => {
    const base = '/contact';
    if (!email.trim()) return `${base}?intent=benchmark_waitlist`;
    return `${base}?intent=benchmark_waitlist&email=${encodeURIComponent(email.trim())}`;
  }, [email]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!email.trim()) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await submitMarketingLead({
        source: 'homepage_waitlist',
        email: email.trim(),
        intent: 'benchmark_waitlist',
        pagePath: window.location.pathname,
      });
      logFirebaseEvent('marketing_lead_capture', {
        slot: 'benchmark_waitlist',
        method: 'api_capture',
      });
      setSubmitted(true);
    } catch (submitError: any) {
      setError(submitError?.message || 'Unable to join waitlist right now.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="bg-[#f8f5ef] px-6 py-20">
      <div className="container mx-auto max-w-5xl rounded-[2rem] border border-slate-200 bg-gradient-to-r from-slate-900 to-slate-800 p-8 text-white md:p-10">
        <div className="grid gap-6 md:grid-cols-[1.1fr_0.9fr] md:items-center">
          <div>
            <p className="inline-flex items-center rounded-full bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-slate-200">
              <Download className="mr-2 h-3.5 w-3.5" />
              Benchmark waitlist
            </p>
            <h2 className="mt-4 font-sans text-3xl text-white md:text-4xl">Get the upcoming financing conversion benchmark for small sellers</h2>
            <p className="mt-3 text-slate-300">
              Join the list for category-specific snapshots, follow-up playbooks, and conversion patterns across teams like vintage furniture shops, studios, and service pros.
            </p>
          </div>

          <div className="rounded-2xl border border-white/20 bg-white/10 p-4">
            {!submitted ? (
              <form onSubmit={handleSubmit} className="space-y-3">
                <label className="block text-xs font-semibold uppercase tracking-[0.14em] text-slate-300">Work email</label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@yourbusiness.com"
                  required
                  className="border-white/30 bg-white text-slate-900 placeholder:text-slate-500"
                />
                <Button type="submit" disabled={isSubmitting} className="w-full rounded-full bg-white text-slate-900 hover:bg-slate-100 disabled:opacity-70">
                  {isSubmitting ? 'Joining...' : 'Join waitlist'}
                </Button>
                <p className="text-xs text-slate-300">No spam. We only email when a meaningful benchmark release is ready.</p>
                {error && <p className="text-xs text-red-200">{error}</p>}
              </form>
            ) : (
              <div className="space-y-3">
                <p className="text-sm font-medium text-emerald-200">You are on the list.</p>
                <p className="text-sm text-slate-200">Want implementation guidance now? Talk with the team and we will map your fastest path to launch.</p>
                <Button asChild variant="outline" className="w-full rounded-full border-white/30 bg-transparent text-white hover:bg-white/10">
                  <a href={contactHref}>
                    Contact team
                    <ArrowUpRight className="ml-2 h-4 w-4" />
                  </a>
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
